import { Checkbox, Modal, TextInput } from '@cointracker/styleguide';
import { Body, Button } from '@cointracker/styleguide/src/LoggedIn';
import { useEffect, useRef, useState } from 'react';
import { useTaxCaseStore } from '../TaxProClients/context';

interface InviteModalProps {
  show: boolean;
  onHide: () => void;
  titleText: string;
  bodyText: string | React.ReactNode;
  loading?: boolean;
}

// Generate available tax years (current year and past 7 years)
const getCurrentYear = () => new Date().getFullYear();
const generateTaxYears = () => {
  const currentYear = getCurrentYear();
  const years = [];
  for (let i = 0; i < 8; i++) {
    years.push(currentYear - i);
  }
  return years;
};

// TODO: Handle errors.
export const CommonInviteModal = ({
  show,
  onHide,
  titleText,
  bodyText,
  loading,
}: InviteModalProps) => {
  const [caseName, setCaseName] = useState('');
  const [selectedTaxYears, setSelectedTaxYears] = useState<number[]>([]);
  const inputRef = useRef<HTMLInputElement>();
  const { addCase } = useTaxCaseStore();
  const availableTaxYears = generateTaxYears();

  const handleTaxYearChange = (year: number, checked: boolean) => {
    if (checked) {
      setSelectedTaxYears((prev) => [...prev, year].sort((a, b) => b - a));
    } else {
      setSelectedTaxYears((prev) => prev.filter((y) => y !== year));
    }
  };

  const handleOnSubmit = async (
    evt:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    evt.preventDefault();

    // Add the case to our store instead of using localStorage directly
    addCase({
      caseName,
      taxYears: selectedTaxYears,
      status: 'Not started',
    });

    hideAndResetEmail();
  };

  const hideAndResetEmail = () => {
    onHide();
  };

  useEffect(() => {
    // Reset form when closed.
    if (!show) {
      setCaseName('');
      setSelectedTaxYears([]);
    } else {
      // Focus on input after showing element. Note: autoFocus doesn't work with Modal.
      inputRef?.current?.focus();
    }
  }, [show]);

  return (
    <Modal.Root open={show} onOpenChange={hideAndResetEmail}>
      <Modal.Content>
        <Modal.ContentHeader title={titleText} />
        <form onSubmit={handleOnSubmit}>
          <Modal.ContentBody className="w-full border-t border-line-tertiary">
            <Body variant="body1" className="block text-text-secondary">
              {bodyText}
            </Body>
            <div className="flex flex-col gap-16 pt-24">
              <TextInput
                onChange={(evt) => setCaseName(evt.target.value.trim())}
                value={caseName}
                label="Case name"
                autoFocus={true}
                autoComplete="off"
                ref={inputRef}
              />
              <div className="flex flex-col gap-16">
                <Body variant="body1" className="block text-text-secondary">
                  Select tax years:
                </Body>
                {availableTaxYears.map((year) => (
                  <Checkbox
                    key={year}
                    label={`${year}`}
                    defaultChecked={year === new Date().getFullYear()}
                    checked={selectedTaxYears.includes(year)}
                    onCheckedChange={(checked) =>
                      handleTaxYearChange(year, !!checked)
                    }
                  />
                ))}
              </div>
            </div>
          </Modal.ContentBody>
          <Modal.ContentFooter className="flex flex-row gap-16">
            <Button
              disabled={loading}
              loading={loading}
              size="small"
              className="w-fit"
              onClick={handleOnSubmit}
            >
              Add case
            </Button>
            <Button
              onClick={hideAndResetEmail}
              variant="line"
              size="small"
              className="w-fit"
            >
              Cancel
            </Button>
          </Modal.ContentFooter>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};
